import { JsonSchema, JsonSchemaTypeName } from "../models";

export const processValue = (
  value: string | number | boolean | undefined | null | Array<string | number>,
  type?: JsonSchemaTypeName | JsonSchemaTypeName[],
  required?: boolean,
  emptyValue?: string | number | boolean | undefined | null,
) => {
  emptyValue = emptyValue !== undefined ? emptyValue : null;

  switch (type) {
    case "array":
      if ((value == null || (Array.isArray(value) && !value.length)) && required) {
        // TODO: Allow setting array-basd emptyValue
        value = [];
      }
      break;
    case "number":
    case "integer":
      value = (value == null || value === ""
        ? required
          ? emptyValue
          : undefined
        : // Remove thousands separators before parsing as Number
          Number((value + "").replace(/,/g, ""))) as number | null | undefined;
      // If the specified value is not a number (e.g. string value), then reset it...
      if (value != null && Number.isNaN(value)) {
        value = required ? emptyValue : undefined;
      }
      break;
    case "boolean":
      value = (value == null || value === "" ? (required ? emptyValue : undefined) : value + "" === "true") as
        | boolean
        | null
        | undefined;
      break;
    case "string":
    default:
      value = value == null || value === "" ? (required ? emptyValue : undefined) : value;
      break;
  }

  return value;
};

// Converts any null properties of the object to undefined...
export const initializeFormData = <T>(someObject: T): T => {
  const obj: { [key: string]: any } = someObject;
  Object.keys(someObject).forEach(key => (obj[key] = obj[key] === null ? undefined : obj[key]));
  return obj as T;
};

export const removeExtraneousFormData = (formData: any, schema: JsonSchema) => {
  // If there is an object to process (i.e. expecting root type of object)...
  if (typeof formData === "object" && formData !== null) {
    removeExtraneousFormDataFromObject(formData, schema);
  }
};

const removeExtraneousFormDataFromObject = (formData: any, schema: JsonSchema) => {
  Object.keys(formData).forEach(propertyName => {
    const propertyValue = formData && formData[propertyName];
    // If this property is an array...
    if (Array.isArray(propertyValue)) {
      const areAllArrayElementsEmpty = propertyValue.every((p: any) => p == null);
      // If this array property does not exist on the schema, or the array elements are "empty"...
      if ((schema.properties && !schema.properties![propertyName]) || areAllArrayElementsEmpty) {
        delete formData[propertyName];
      }
      // Otherwise...
      else {
        // Further process each array element...
        propertyValue.forEach(propertyValueElement => {
          // If the array element is an object, then recurse...
          if (typeof propertyValueElement === "object" && propertyValueElement !== null) {
            let arrayPropertySchema = (schema.properties![propertyName] as JsonSchema).items as JsonSchema;
            // If the items are oneOf or anyOf multiple sub-schemas...
            if (arrayPropertySchema.oneOf || arrayPropertySchema.anyOf) {
              const arrayPropertySubSchemas = (arrayPropertySchema.oneOf || arrayPropertySchema.anyOf) as JsonSchema[];

              let bestSchemaMatchScore = 0;
              let bestSchemaMatchIndex = 0;

              arrayPropertySubSchemas.forEach((arrayPropertySubSchema, index) => {
                const subSchemaPropertyNames = Object.keys(arrayPropertySubSchema.properties!);
                const formDataPropertyNames = Object.keys(propertyValueElement);
                const matchingSubSchemaPropertyNames = subSchemaPropertyNames.filter(pn =>
                  formDataPropertyNames.includes(pn),
                );
                // Schema score is based on the average property matching rate of the current schema and the
                // eligible sub-schema, and the current formData and eligible sub-schema
                const schemaMatchScore =
                  (matchingSubSchemaPropertyNames.length / subSchemaPropertyNames.length +
                    matchingSubSchemaPropertyNames.length / formDataPropertyNames.length) /
                  2;
                if (schemaMatchScore > bestSchemaMatchScore) {
                  bestSchemaMatchScore = schemaMatchScore;
                  bestSchemaMatchIndex = index;
                }
              });

              arrayPropertySchema = arrayPropertySubSchemas[bestSchemaMatchIndex];
            }

            removeExtraneousFormDataFromObject(propertyValueElement, arrayPropertySchema);
          }
        });
      }
    }
    // Otherwise, if this property is an object...
    else if (typeof propertyValue === "object" && propertyValue !== null) {
      // If this property does not exist on the schema...
      if (!schema.properties![propertyName]) {
        delete formData[propertyName];
      }
      // Otherwise, recurse...
      else {
        const objectPropertySchema = schema.properties![propertyName] as JsonSchema;
        removeExtraneousFormDataFromObject(propertyValue, objectPropertySchema);
      }
    }
    // Otherwise, assume this property is a primitive...
    else {
      // If this property does not exist on the schema, or is an "empty" value...
      if (!schema.properties![propertyName] || propertyValue === "" || propertyValue === null) {
        delete formData[propertyName];
      }
    }
  });
};
